import React, { useEffect, useState } from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmtAud = (cents) => `A$${(cents / 100).toFixed(2)}`;

export function CheckoutDialog({ open, onOpenChange, cycle, price, onSuccess }) {
  const { refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open && !config) {
      http.get("/billing/config").then(({ data }) => setConfig(data)).catch(() => toast.error("Could not load payment form."));
    }
  }, [open, config]);

  const handleToken = async (token) => {
    if (!token?.token) { toast.error("Card details could not be read. Please try again."); return; }
    setProcessing(true);
    try {
      await http.post("/billing/checkout", { cycle, source_id: token.token });
      await refresh();
      toast.success("You're subscribed — welcome to Framer's Bench!");
      onOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const cycleLabel = cycle === "annual" ? "Annual subscription" : "Monthly subscription";

  return (
    <Dialog open={open} onOpenChange={(o) => !processing && onOpenChange(o)}>
      <DialogContent className="max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Subscribe to Framer's Bench</DialogTitle>
          <DialogDescription>
            {cycleLabel} · <span className="font-semibold text-foreground">{fmtAud(price)}{cycle === "annual" ? "/yr" : "/mo"}</span>
          </DialogDescription>
        </DialogHeader>

        {!config ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading secure payment…
          </div>
        ) : (
          <div data-testid="square-payment-form">
            <PaymentForm
              applicationId={config.application_id}
              locationId={config.location_id}
              cardTokenizeResponseReceived={handleToken}
            >
              <CreditCard buttonProps={{ isLoading: processing }} />
            </PaymentForm>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> Secured by Square. Cancel anytime.
            </div>
            {config.environment === "sandbox" && (
              <p className="mt-2 rounded-md bg-secondary p-2 text-[11px] text-muted-foreground">
                Sandbox test card: <span className="font-mono">4111 1111 1111 1111</span>, any future expiry, CVV 111, postcode 2000.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
